import type { GameState } from "@dailywars/core/engine";
import { coordKey, isBuildingTile } from "@dailywars/core/engine";

import { drawBuildingImage } from "#/game/buildingDraw";
import { isImageReady } from "#/game/imageReady";
import { drawRoadAutotileImage } from "#/game/roadAutotileDraw";
import { resolveRoadAutotile } from "#/game/roadSprites";
import { drawRoadTerrainBackground } from "#/game/roadTerrainBackground";
import { terrainKindForTile } from "#/game/roadTileIcon";
import { getBuildingImage, getOccupiedBuildingImage } from "#/game/svgAssets";
import { drawTerrainTile } from "#/game/terrainTileDraw";

import { tileIndex, TILE_SIZE } from "./boardConstants";

export type MapDrawState = Pick<GameState, "width" | "height" | "tiles" | "theme">;

export function drawTerrainLayer(ctx: CanvasRenderingContext2D, state: MapDrawState): void {
  for (let y = 0; y < state.height; y++) {
    for (let x = 0; x < state.width; x++) {
      const tile = state.tiles[tileIndex({ x, y, width: state.width })];
      if (tile.kind === "road") {
        drawRoadTerrainBackground({
          ctx,
          tiles: state.tiles,
          width: state.width,
          x,
          y,
          theme: state.theme,
          tileSize: TILE_SIZE,
        });
        continue;
      }
      const terrainKind = terrainKindForTile(tile.kind);
      drawTerrainTile({
        ctx,
        kind: terrainKind,
        x,
        y,
        theme: state.theme,
        tileSize: TILE_SIZE,
        tiles: state.tiles,
        width: state.width,
      });
    }
  }
}

export function drawRoadStripesLayer(ctx: CanvasRenderingContext2D, state: MapDrawState): void {
  for (let y = 0; y < state.height; y++) {
    for (let x = 0; x < state.width; x++) {
      const tile = state.tiles[tileIndex({ x, y, width: state.width })];
      if (tile.kind !== "road") continue;
      const { img, rotation } = resolveRoadAutotile({
        tiles: state.tiles,
        width: state.width,
        x,
        y,
        theme: state.theme,
      });
      if (isImageReady(img)) {
        drawRoadAutotileImage({ ctx, img, tileX: x, tileY: y, tileSize: TILE_SIZE, rotation });
      }
    }
  }
}

export function drawBuildingsLayer({
  ctx,
  state,
  occupiedTileKeys,
}: {
  ctx: CanvasRenderingContext2D;
  state: MapDrawState;
  occupiedTileKeys?: ReadonlySet<string>;
}): void {
  for (let y = 0; y < state.height; y++) {
    for (let x = 0; x < state.width; x++) {
      const tile = state.tiles[tileIndex({ x, y, width: state.width })];
      if (!isBuildingTile(tile.kind)) continue;
      const isOccupied = occupiedTileKeys?.has(coordKey({ x, y })) ?? false;
      const bImg = isOccupied
        ? getOccupiedBuildingImage(tile.kind, tile.owner ?? "neutral")
        : getBuildingImage(tile.kind, tile.owner ?? "neutral");
      if (!isImageReady(bImg)) continue;

      drawBuildingImage({ ctx, img: bImg, tileX: x, tileY: y, tileSize: TILE_SIZE });
    }
  }
}

export function drawMapLayers(ctx: CanvasRenderingContext2D, state: MapDrawState): void {
  drawTerrainLayer(ctx, state);
  drawRoadStripesLayer(ctx, state);
  drawBuildingsLayer({ ctx, state });
}
